// src/pages/TestimoniesPage.js
// Updated: 12/01/2025 06:55 PM

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, onSnapshot, addDoc, doc, deleteDoc, serverTimestamp } from 'firebase/firestore'; 
import { getFunctions, httpsCallable } from 'firebase/functions';

import TestimonialCard from '../components/TestimonialCard';
import TestimonialModal from '../components/TestimonialModal';

import { Button, Container, Typography, CircularProgress, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

function TestimoniesPage() {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [publicTestimonials, setPublicTestimonials] = useState([]);
  const [myPrivateTestimonials, setMyPrivateTestimonials] = useState([]);
  const [allPrivateTestimonials, setAllPrivateTestimonials] = useState([]);

  const functions = getFunctions();
  const reactToTestimonialFn = httpsCallable(functions, 'reactToTestimonial');

  useEffect(() => {
    if (!userProfile?.churchId || !currentUser?.uid) {
      setLoading(false);
      return;
    }

    const testimonialsColRef = collection(db, 'churches', userProfile.churchId, 'testimonials');
    const canViewAllPrivate = userProfile.canViewPrivatePrayers === true || userProfile.role === 'Super Administrator';

    const publicQuery = query(testimonialsColRef, where('isPrivate', '==', false));
    const unsubscribePublic = onSnapshot(publicQuery, (snapshot) => {
      setPublicTestimonials(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching public testimonials: ", error);
      setLoading(false);
    });

    let unsubscribeMyPrivate = () => {};
    let unsubscribeAllPrivate = () => {};

    const myPrivateQuery = query(
      testimonialsColRef,
      where('isPrivate', '==', true),
      where('authorId', '==', currentUser.uid)
    );
    unsubscribeMyPrivate = onSnapshot(myPrivateQuery, (snapshot) => {
      setMyPrivateTestimonials(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    if (canViewAllPrivate) {
      const allPrivateQuery = query(testimonialsColRef, where('isPrivate', '==', true));
      unsubscribeAllPrivate = onSnapshot(allPrivateQuery, (snapshot) => {
        setAllPrivateTestimonials(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      });
    }

    return () => {
      unsubscribePublic();
      unsubscribeMyPrivate();
      unsubscribeAllPrivate();
    };
  }, [userProfile, currentUser]);

  const testimonials = [...publicTestimonials, ...myPrivateTestimonials, ...allPrivateTestimonials]
    .reduce((acc, current) => {
        if (!acc.find(item => item.id === current.id)) {
            acc.push(current);
        }
        return acc;
    }, [])
    .sort((a, b) => (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0));

  const handleSaveTestimonial = async (data) => {
    if (!userProfile?.churchId) return;
    setIsSubmitting(true);
    try {
      const colRef = collection(db, 'churches', userProfile.churchId, 'testimonials');
      await addDoc(colRef, {
        ...data,
        authorName: data.isAnonymous ? 'Anonymous' : userProfile.firstName,
        authorId: currentUser.uid,
        reactionCount: 0,
        reactedByUids: [],
        createdAt: serverTimestamp(),
      });
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error submitting testimony:", error);
      alert(`Failed to share testimony: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!userProfile?.churchId) return;
    if (window.confirm('Are you sure you want to delete this testimony?')) {
      const docRef = doc(db, 'churches', userProfile.churchId, 'testimonials', id);
      await deleteDoc(docRef);
    }
  };

  const handleReact = async (testimonialId) => {
    if (!currentUser?.uid || !userProfile?.churchId) {
      alert("You must be logged in to react.");
      return;
    }
    try {
      const result = await reactToTestimonialFn({ testimonialId, churchId: userProfile.churchId });
      if (result.data.message === "Already reacted to this testimonial.") {
        alert(result.data.message);
      }
    } catch (error) {
      console.error("Error reacting to testimony:", error);
      alert(`Failed to react: ${error.message}`);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!userProfile) {
    return <Container><Typography>Could not load user profile.</Typography></Container>;
  }

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Testimony Wall</Typography> {/* UPDATED HEADER */}
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsModalOpen(true)}>
          Share a Testimony
        </Button>
      </Box>

      {!testimonials.length && !loading && (
        <Typography>Be the first to share a testimony.</Typography>
      )}

      {testimonials.map(item => (
        <TestimonialCard
          key={item.id}
          testimonial={item}
          currentUser={currentUser}
          userRole={userProfile?.role}
          onDelete={handleDelete}
          onReact={handleReact}
        />
      ))}

      <TestimonialModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveTestimonial}
        loading={isSubmitting}
      />
    </Container>
  );
}

export default TestimoniesPage;